import type { ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';

import { usePermissions } from '../../contexts/AuthContext';
import { P } from '../../utils/permissions';

/**
 * Projects → Reports moved to Reports → Profitability (D4). Its filters keep
 * their `pr*` names there, so the old link's own filters carry across as they
 * are, and an `?tab=reports&prPreset=all-time` bookmark opens on All time
 * (AC-34, AC-E7).
 */
export function profitabilityUrlFor(projectsParams: URLSearchParams): string | null {
  if (projectsParams.get('tab') !== 'reports') return null;
  const next = new URLSearchParams({ tab: 'profitability' });
  for (const [key, value] of projectsParams) {
    if (key.startsWith('pr')) next.append(key, value);
  }
  return `/reports?${next.toString()}`;
}

/**
 * Sends an old Projects → Reports link to Reports → Profitability, and renders
 * `children` (the Projects page behind its own gate) for any other link. It
 * wraps the gate rather than sitting inside it, so a reader the report is for
 * follows the link whether or not they may open Projects; Reports applies its
 * own gate on arrival (docs/architecture/design-system.md, Locked navigation).
 *
 * Only a reader who may open Reports at all (`view:reports:operational`, the
 * page's route gate) is sent there; anyone else would bounce on to the
 * Dashboard. For them the link falls through to the Projects page, which opens
 * its Projects tab for a tab it no longer has.
 */
export function ProfitabilityRedirect({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const moved = hasPermission(P.VIEW_REPORTS_OPERATIONAL) ? profitabilityUrlFor(searchParams) : null;
  return moved ? <Navigate to={moved} replace /> : <>{children}</>;
}
